import React, { useEffect, useState } from 'react';
import TokenSelector from '../allocation/TokenSelector';

const SetTokenModal = ({ isOpen, selectedCount, onClose, onSave, loading }) => {
  const [selectedToken, setSelectedToken] = useState(null);
  const [activeSelectorIndex, setActiveSelectorIndex] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedToken(null);
      setError(null);
      setActiveSelectorIndex(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (activeSelectorIndex !== null) {
        setActiveSelectorIndex(null);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeSelectorIndex, onClose]);

  const handleTokenSelected = (token) => {
    setSelectedToken(token);
    setError(null);
    setActiveSelectorIndex(null);
  };

  const toggleSelector = (index) => {
    setActiveSelectorIndex((prev) => (prev === index ? null : index));
  };

  // Token is valid if it has either a database ID or a coingecko API ID
  const hasValidToken = selectedToken?.tokenId || selectedToken?.coingeckoApiId;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedToken) {
      setError('Please select a token');
      return;
    }
    if (!hasValidToken) {
      setError('Invalid token selection');
      return;
    }
    // Pass both tokenId (if exists) and coingeckoApiId
    onSave({
      tokenId: selectedToken.tokenId || null,
      coingeckoApiId: selectedToken.coingeckoApiId || null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Set Token for {selectedCount} record{selectedCount === 1 ? '' : 's'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Select Token
            </label>
            <TokenSelector
              selectedTokenId={selectedToken?.tokenId || selectedToken?.id || ''}
              selectedToken={selectedToken}
              selectedCoingeckoApiId={selectedToken?.coingeckoApiId || null}
              onSelectToken={handleTokenSelected}
              index={0}
              isActive={activeSelectorIndex === 0}
              toggleActive={toggleSelector}
              existingTokenIds={[]}
              existingCoingeckoApiIds={[]}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={loading || !hasValidToken}
            >
              {loading ? 'Applying...' : 'Apply to Selected'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetTokenModal;

